import { useEffect } from "react"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { useParams } from "react-router-dom"
import { PageHeader } from "@/widgets/page-header"
import { PageSubtitle } from "@/shared/ui/page-subtitle"
import { StatisticsBlock } from "@/shared/ui/statistics-block"
import { DashboardTransactionsTable } from "@/entities/onboarding"
import { Pagination } from "@/shared/ui/pagination"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import {
    fetchBusinessCard,
    fetchBusinessCardTransactions,
    selectBusinessCard,
    selectBusinessCardTransactions,
    selectBusinessCardTransactionDetails,
    selectBusinessCardLoading,
    selectBusinessCardFirstLoad,
    selectBusinessCardPage,
    selectBusinessCardTotalPages,
    setPage,
    resetState
} from "../model"
import { toLocaleString } from "@/shared/lib/to-locale-string"
import ListBulletsSvg from '@/shared/assets/images/ListBullets.svg'
import MoneySvg from '@/shared/assets/images/Money.svg'
import PercentSvg from '@/shared/assets/images/Percent.svg'
import ChartBarSvg from '@/shared/assets/images/ChartBar.svg'

const formatCardNumber = (number: string): string => {
    if (!number) return ''
    const last4 = number.slice(-4)
    return `•••• ${last4}`
}

export const BusinessCardDetailsPage = () => {
    const { id } = useParams<{ id: string }>()
    const dispatch = useAppDispatch()
    const card = useAppSelector(selectBusinessCard)
    const transactions = useAppSelector(selectBusinessCardTransactions)
    const transactionDetails = useAppSelector(selectBusinessCardTransactionDetails)
    const isLoading = useAppSelector(selectBusinessCardLoading)
    const isFirstLoad = useAppSelector(selectBusinessCardFirstLoad)
    const page = useAppSelector(selectBusinessCardPage)
    const totalPages = useAppSelector(selectBusinessCardTotalPages)

    useEffect(() => {
        if (id) {
            dispatch(resetState())
            dispatch(fetchBusinessCard(id))
        }
    }, [id, dispatch])

    useEffect(() => {
        if (id) {
            dispatch(fetchBusinessCardTransactions({ cardId: id, page, limit: 20 }))
        }
    }, [id, page, dispatch])

    return (
        <div className="_page_container">
            <PageHeader title="Детали бизнес-карты" />
            {card && (
                <div className='flexbox'>
                    <PageSubtitle title={`Карта ${formatCardNumber(card.number)}`} />
                    <div className="box">
                        <div className="box_header !mb-4 screen:!mb-[22px] font-mediumttf">
                            Информация о карте
                        </div>
                        <div className="flex flex-col">
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">Номер карты</div>
                                <div className="text-[13px] text-color-dark">
                                    {formatCardNumber(card.number)}
                                </div>
                            </div>
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">Платёжная система</div>
                                <div className="text-[13px] text-color-dark">
                                    {card.paymentSystemTypeName ?? "—"}
                                </div>
                            </div>
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">Банк</div>
                                <div className="text-[13px] text-color-dark">
                                    {card.bankTypeName ?? "—"}
                                </div>
                            </div>
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">Статус</div>
                                <div className="text-[13px] text-color-dark">
                                    {card.status === 'active' ? 'Активна' : 'Неактивна'}
                                </div>
                            </div>
                        </div>
                    </div>
                    <PageSubtitle title="Статистика транзакций" />
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-6 xxl:col-span-3">
                            <StatisticsBlock
                                className="screen:!py-5"
                                icon={ListBulletsSvg}
                                label='Количество транзакций'
                                value={toLocaleString(transactionDetails?.transactionCount || 0)}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-6 xxl:col-span-3">
                            <StatisticsBlock
                                className="screen:!py-5"
                                icon={MoneySvg}
                                label='Сумма транзакций'
                                value={`${toLocaleString(transactionDetails?.totalSum || 0)} ₽`}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-6 xxl:col-span-3">
                            <StatisticsBlock
                                className="screen:!py-5"
                                icon={PercentSvg}
                                label='Сумма скидок'
                                value={`${toLocaleString(transactionDetails?.totalDiscount || 0)} ₽`}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-6 xxl:col-span-3">
                            <StatisticsBlock
                                className="screen:!py-5"
                                icon={ChartBarSvg}
                                label='Сумма НДС'
                                value={`${toLocaleString(transactionDetails?.totalTax || 0)} ₽`}
                            />
                        </div>
                    </div>
                    <PageSubtitle title="Транзакции" />
                    <DashboardTransactionsTable transactions={transactions} isLoading={isLoading} />
                    {Boolean(transactions.length) && totalPages > 1 && (
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            setPage={(newPage) => dispatch(setPage(newPage))}
                        />
                    )}
                </div>
            )}
            {(!card && isLoading) && (
                <div className="info_panel">
                    <Loader />
                </div>
            )}
            {(!card && !isLoading) && (
                <NoTableData label="Не удалось найти карту"/>
            )}
        </div>
    )
}

